'use client';

/**
 * AstorQuickAskOverlay Component
 *
 * Full-viewport "Ask Astor" surface triggered from the business-portal topbar
 * (sparkle button or ⌘K). Page-context aware — suggestions are derived from
 * the current route by the caller and passed in via `suggestions`.
 *
 * Behavior:
 *   - Top input bar with a sparkle prefix icon, autoFocus on open, Enter
 *     submits, ⌘+Enter also submits.
 *   - Suggestion grid below the input — pre-localized chips that submit on
 *     click (or just stage the prompt — caller decides via `onSelectSuggestion`).
 *   - "Send to Astor →" button submits the staged prompt and hands off to the
 *     full /astor surface (the caller wires the navigation).
 *
 * Reference: `.claude/design-handoff-v3/project/pages/auth-overlays.jsx`
 * `OverlayAstor` (full-viewport variant of `AstorQuickAskPopover`).
 *
 * §11b: Glass primitives only (GlassButton, GlassTextField, GlassChip).
 * §11b2: Typography variants only, no hardcoded fontSize.
 */

import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardCommandKeyIcon from '@mui/icons-material/KeyboardCommandKey';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type KeyboardEvent, type ReactNode, useEffect, useId, useState } from 'react';
import { useI18n } from '../../hooks';
import { iconSizes } from '../../theme/typography';
import { GlassButton } from '../GlassButton';
import { GlassIconButton } from '../GlassIconButton';
import { GlassTextField } from '../GlassTextField';

// ============================================================================
// Types
// ============================================================================

export interface AstorOverlayPageContext {
  /** Human-readable surface label ("Loan detail", "Dashboard"). */
  surface: string;
  /** Optional entity id (e.g., case id, partner id). Used only for display. */
  entityId?: string;
}

export interface AstorQuickAskOverlayProps {
  /** Whether the overlay is open. */
  open: boolean;
  /** Close handler — fires on Esc, backdrop click, and the X button. */
  onClose: () => void;
  /** Context label rendered under the header. */
  pageContext: AstorOverlayPageContext;
  /**
   * Pre-localized suggestions (caller picks based on `pageContext`).
   * Empty array → shows `emptyState`.
   */
  suggestions: string[];
  /**
   * Called when the user submits the prompt (Enter / Send button / suggestion
   * click). The caller routes to /astor with the prompt as a prefill.
   */
  onSubmit: (prompt: string) => void;
  /** Optional empty-state for the suggestions grid. */
  emptyState?: ReactNode;
  /** Optional model label rendered in the header (e.g., "haiku-4.5"). */
  modelName?: string;
  /** Style overrides. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Component
// ============================================================================

const MONO = 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace';

export function AstorQuickAskOverlay({
  open,
  onClose,
  pageContext,
  suggestions,
  onSubmit,
  emptyState,
  modelName,
  sx,
}: AstorQuickAskOverlayProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const titleId = useId();
  const isDark = theme.palette.mode === 'dark';

  const mint = theme.palette.success.main;
  const mintBorder = alpha(mint, isDark ? 0.22 : 0.18);
  const mintInsetGlow = alpha(mint, isDark ? 0.06 : 0.05);

  const [value, setValue] = useState('');

  // Reset the input each time the overlay opens — stale text from a prior
  // session is worse than an empty field.
  useEffect(() => {
    if (open) setValue('');
  }, [open]);

  const submitDisabled = value.trim().length === 0;

  const submit = (text?: string) => {
    const trimmed = (text ?? value).trim();
    if (trimmed.length === 0) return;
    onSubmit(trimmed);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const contextLabel = pageContext.entityId ? `${pageContext.surface} · ${pageContext.entityId}` : pageContext.surface;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(theme.palette.background.default, 0.66),
            backdropFilter: 'blur(8px) saturate(140%)',
            WebkitBackdropFilter: 'blur(8px) saturate(140%)',
          },
        },
      }}
      sx={{
        display: 'flex',
        // Anchor near the top: the quick-ask is a "drop down from the
        // sparkle button" semantic. md+: 12vh top margin reads as overlay,
        // xs: hugs the top.
        alignItems: 'flex-start',
        justifyContent: 'center',
        pt: { xs: 2, md: '12vh' },
        px: { xs: 2, md: 4 },
        pb: { xs: 2, md: 4 },
        zIndex: theme.zIndex.modal,
      }}
    >
      <Paper
        role="dialog"
        aria-labelledby={titleId}
        elevation={0}
        sx={[
          {
            width: '100%',
            maxWidth: 720,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: { xs: '12px', md: '14px' },
            overflow: 'hidden',
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: mintBorder,
            boxShadow: `${theme.shadows[16]}, inset 0 0 0 1px ${mintInsetGlow}`,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flexShrink: 0,
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 30,
              height: 30,
              borderRadius: '8px',
              border: '1px solid',
              borderColor: mintBorder,
              backgroundColor: alpha(mint, isDark ? 0.18 : 0.14),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AutoAwesomeOutlinedIcon sx={{ fontSize: iconSizes.sm, color: 'success.main' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id={titleId}
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {t('overlays.astor.title')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontFamily: MONO,
                color: 'text.secondary',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {modelName ? `${modelName} · ${contextLabel}` : contextLabel}
            </Typography>
          </Box>
          <Box
            aria-hidden
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 0.5,
              px: 0.75,
              py: 0.25,
              borderRadius: '6px',
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.disabled',
            }}
          >
            <KeyboardCommandKeyIcon sx={{ fontSize: iconSizes['2xs'] }} />
            <Typography variant="overline" sx={{ fontFamily: MONO, letterSpacing: '0.08em', lineHeight: 1 }}>
              K
            </Typography>
          </Box>
          <GlassIconButton size="small" onClick={onClose} aria-label={t('overlays.astor.close')}>
            <CloseRoundedIcon sx={{ fontSize: iconSizes.md }} />
          </GlassIconButton>
        </Box>

        {/* Body */}
        <Box sx={{ px: 2.5, py: 2.25 }}>
          <GlassTextField
            fullWidth
            // 4-row textarea so multi-line prompts (e.g. "Walk through how
            // we'd structure a $1.2M bridge for an out-of-state borrower
            // with a 720 FICO") have room without growing the overlay.
            multiline
            minRows={3}
            maxRows={5}
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('overlays.astor.placeholder')}
            aria-label={t('overlays.astor.placeholder')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.25 }}>
                    <AutoAwesomeOutlinedIcon aria-hidden sx={{ fontSize: iconSizes.sm, color: 'success.main' }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ mb: 2 }}
          />

          {/* Suggestions */}
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              fontFamily: MONO,
              color: 'text.disabled',
              letterSpacing: '0.08em',
              fontWeight: 600,
              mb: 1,
            }}
          >
            {t('overlays.astor.suggestionsHeader')}
          </Typography>

          {suggestions.length === 0 ? (
            <Box
              sx={{
                py: 2.5,
                textAlign: 'center',
                borderRadius: '7px',
                border: '1px dashed',
                borderColor: 'divider',
                backgroundColor: 'background.default',
              }}
            >
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {emptyState ?? t('overlays.astor.empty')}
              </Typography>
            </Box>
          ) : (
            <Box
              role="list"
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 1,
              }}
            >
              {suggestions.map((suggestion) => (
                <SuggestionRow
                  key={suggestion}
                  text={suggestion}
                  onClick={() => {
                    setValue(suggestion);
                    submit(suggestion);
                  }}
                />
              ))}
            </Box>
          )}

          {/* Footer */}
          <Box
            sx={{
              mt: 2.25,
              pt: 1.75,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.25,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {t('overlays.astor.hint')}
            </Typography>
            <GlassButton
              variant="gradient"
              color="success"
              size="medium"
              startIcon={<SendRoundedIcon sx={{ fontSize: iconSizes.sm }} aria-hidden />}
              onClick={() => submit()}
              disabled={submitDisabled}
              aria-disabled={submitDisabled}
            >
              {t('overlays.astor.send')}
            </GlassButton>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
}

AstorQuickAskOverlay.displayName = 'AstorQuickAskOverlay';

// ============================================================================
// Sub-components
// ============================================================================

function SuggestionRow({ text, onClick }: { text: string; onClick: () => void }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const mint = theme.palette.success.main;
  const hoverBg = alpha(mint, isDark ? 0.08 : 0.06);

  return (
    <Box
      component="button"
      type="button"
      role="listitem"
      onClick={onClick}
      sx={{
        width: '100%',
        px: 1.5,
        py: 1.25,
        borderRadius: '8px',
        backgroundColor: 'background.default',
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: 'inherit',
        color: 'inherit',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        minHeight: 44,
        '&:hover': {
          backgroundColor: hoverBg,
          borderColor: 'success.main',
        },
        '&:focus-visible': {
          outline: `2px solid ${mint}`,
          outlineOffset: -2,
        },
      }}
    >
      <AutoAwesomeOutlinedIcon aria-hidden sx={{ fontSize: iconSizes.sm, color: 'success.main', flexShrink: 0 }} />
      <Typography
        variant="body2"
        component="span"
        sx={{
          flex: 1,
          color: 'text.primary',
          lineHeight: 1.4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}
